import { Maximize2 } from "lucide-react";

interface PowerBIEmbedProps {
  /** Feeds the frame header label, e.g. "External — investor & facility view" */
  productName: string;
  /** Power BI embed / report URL */
  src?: string;
  /** Fixed frame body height in px. The frame never resizes. */
  height?: number;
  /** Still resolving the report details from the backend. */
  loading?: boolean;
  /** Shown in place of the frame when there is no `src`. Defaults to a generic line;
   *  the Snapshot page passes something that explains the report is pending credentials
   *  rather than broken. */
  emptyMessage?: string;
}

export function PowerBIEmbed({
  productName,
  src,
  height = 480,
  loading = false,
  emptyMessage,
}: PowerBIEmbedProps) {
  return (
    <section className="overflow-hidden rounded-lg border border-border bg-card">
      <header className="flex items-center justify-between gap-4 border-b border-border px-5 py-3">
        <h3 className="text-sm font-semibold text-card-foreground">{productName}</h3>
      </header>

      <div className="relative bg-background" style={{ height }}>
        {src ? (
          <iframe
            title={productName}
            src={src}
            className="h-full w-full border-0"
            allowFullScreen={false}
          />
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-2 px-6 text-center text-sm text-muted-foreground">
            {loading
              ? "Loading report…"
              : emptyMessage ?? "No report URL configured"}
          </div>
        )}

        {src && (
          <a
            href={src}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Open ${productName} in a new tab`}
            className="absolute bottom-4 right-4 inline-flex h-9 w-9 items-center justify-center rounded-md bg-accent text-accent-foreground shadow-md transition-colors hover:bg-accent/80"
          >
            <Maximize2 className="h-4 w-4" />
          </a>
        )}
      </div>
    </section>
  );
}
